/**
 * Regional Formatter Utilities
 *
 * Centralized formatting for currency, distance, and fuel consumption
 * with regional support for EU, UK, and US markets.
 *
 * Related Spec: uk-us-expansion
 * Task: Task 9 - Regional Formatter Utilities
 */

/**
 * Regional locale and currency configuration
 */
const REGION_CONFIG = {
  EU: {
    locale: 'de-DE',
    currency: 'EUR',
    currencySymbol: '€',
    distanceUnit: 'km',
    fuelUnit: 'L/100km',
  },
  UK: {
    locale: 'en-GB',
    currency: 'GBP',
    currencySymbol: '£',
    distanceUnit: 'miles',
    fuelUnit: 'MPG',
  },
  US: {
    locale: 'en-US',
    currency: 'USD',
    currencySymbol: '$',
    distanceUnit: 'miles',
    fuelUnit: 'MPG',
  },
};

/**
 * Fuel price configuration for each region
 * Contains min/max/default values and units for fuel price sliders
 * Updated December 2025 based on current market prices:
 * - US: $2.95/gal avg (range $2.41-$4.51)
 * - UK: £1.37/L avg (range £1.30-£1.45)
 * - EU: €1.54/L avg (range €1.20-€1.98)
 */
export const FUEL_PRICE_CONFIG = {
  EU: {
    min: 1.0,
    max: 2.5,
    default: 1.55,
    symbol: '€',
    unit: 'per L',
    step: 0.05,
  },
  UK: {
    min: 1.2,
    max: 1.7,
    default: 1.37,
    symbol: '£',
    unit: 'per L',
    step: 0.05,
  },
  US: {
    min: 2.0,
    max: 5.0,
    default: 3.0,
    symbol: '$',
    unit: 'per gal',
    step: 0.1,
  },
};

/**
 * Distance tier mappings for onboarding questions
 * Maps abstract IDs to display strings per region
 */
export const DISTANCE_TIER_MAPPINGS = {
  budget: {
    EU: {
      budget_tier_1: '€0 - €5,000',
      budget_tier_2: '€5,000 - €10,000',
      budget_tier_3: '€10,000 - €15,000',
      budget_tier_4: '€15,000 - €20,000',
      budget_tier_5: '€20,000 - €30,000',
      budget_tier_6: '€30,000 - €50,000',
      budget_tier_7: '€50,000+',
    },
    UK: {
      budget_tier_1: '£0 - £5,000',
      budget_tier_2: '£5,000 - £10,000',
      budget_tier_3: '£10,000 - £15,000',
      budget_tier_4: '£15,000 - £20,000',
      budget_tier_5: '£20,000 - £30,000',
      budget_tier_6: '£30,000 - £50,000',
      budget_tier_7: '£50,000+',
    },
    US: {
      budget_tier_1: '$0 - $10,000',
      budget_tier_2: '$10,000 - $15,000',
      budget_tier_3: '$15,000 - $20,000',
      budget_tier_4: '$20,000 - $30,000',
      budget_tier_5: '$30,000 - $40,000',
      budget_tier_6: '$40,000 - $60,000',
      budget_tier_7: '$60,000+',
    },
  },
  distance: {
    EU: {
      distance_tier_1: '0 - 5,000 km/year',
      distance_tier_2: '5,000 - 10,000 km/year',
      distance_tier_3: '10,000 - 15,000 km/year',
      distance_tier_4: '15,000 - 20,000 km/year',
      distance_tier_5: '20,000+ km/year',
    },
    UK: {
      distance_tier_1: '0 - 3,000 miles/year',
      distance_tier_2: '3,000 - 7,000 miles/year',
      distance_tier_3: '7,000 - 10,000 miles/year',
      distance_tier_4: '10,000+ miles/year',
    },
    US: {
      distance_tier_1: '0 - 3,000 miles/year',
      distance_tier_2: '3,000 - 7,000 miles/year',
      distance_tier_3: '7,000 - 10,000 miles/year',
      distance_tier_4: '10,000+ miles/year',
    },
  },
};

/**
 * Formats currency amount with regional locale and currency
 *
 * @param {number} amount - The amount to format
 * @param {'EU'|'UK'|'US'} region - The region for formatting
 * @returns {string} Formatted currency string (e.g., "€1,234.56", "£1,234.56", "$1,234.56")
 */
export function formatCurrency(amount, region = 'EU') {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '-';
  }

  const config = REGION_CONFIG[region] || REGION_CONFIG.EU;

  try {
    return new Intl.NumberFormat(config.locale, {
      style: 'currency',
      currency: config.currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (error) {
    console.error('[formatCurrency] Error formatting currency:', error);
    return `${config.currencySymbol}${amount.toFixed(2)}`;
  }
}

/**
 * Formats distance with regional unit (km or miles)
 *
 * @param {number} distance - The distance value
 * @param {'EU'|'UK'|'US'} region - The region for unit selection
 * @returns {string} Formatted distance string (e.g., "1000 km", "621 miles")
 */
export function formatDistance(distance, region = 'EU') {
  if (distance === null || distance === undefined || isNaN(distance)) {
    return '-';
  }

  const config = REGION_CONFIG[region] || REGION_CONFIG.EU;
  const roundedDistance = Math.round(distance);

  return `${roundedDistance.toLocaleString()} ${config.distanceUnit}`;
}

/**
 * Formats fuel consumption with regional unit
 *
 * @param {number} consumption - Fuel consumption value
 * @param {'EU'|'UK'|'US'} region - The region for unit selection
 * @returns {string} Formatted fuel consumption (e.g., "7.5 L/100km", "40 MPG")
 */
export function formatFuelConsumption(consumption, region = 'EU') {
  if (consumption === null || consumption === undefined || isNaN(consumption)) {
    return '-';
  }

  const config = REGION_CONFIG[region] || REGION_CONFIG.EU;

  // For EU, show one decimal place (L/100km)
  // For UK/US, show whole numbers (MPG)
  const formatted = region === 'EU'
    ? consumption.toFixed(1)
    : Math.round(consumption).toString();

  return `${formatted} ${config.fuelUnit}`;
}

/**
 * Gets budget tier display string for a given tier ID and region
 *
 * @param {string} tierId - Abstract tier ID (e.g., 'budget_tier_3')
 * @param {'EU'|'UK'|'US'} region - The region
 * @returns {string} Display string for the tier
 */
export function getBudgetTierDisplay(tierId, region = 'EU') {
  return DISTANCE_TIER_MAPPINGS.budget[region]?.[tierId] || tierId;
}

/**
 * Gets distance tier display string for a given tier ID and region
 *
 * @param {string} tierId - Abstract tier ID (e.g., 'distance_tier_2')
 * @param {'EU'|'UK'|'US'} region - The region
 * @returns {string} Display string for the tier
 */
export function getDistanceTierDisplay(tierId, region = 'EU') {
  return DISTANCE_TIER_MAPPINGS.distance[region]?.[tierId] || tierId;
}

/**
 * Validates if a region string is a valid region enum value
 *
 * @param {string} region - The region to validate
 * @returns {boolean} True if valid region
 */
export function isValidRegion(region) {
  return ['EU', 'UK', 'US'].includes(region);
}

/**
 * Gets the default region (EU)
 *
 * @returns {'EU'} The default region
 */
// Formats a cooldown remaining duration (in minutes) as a human-friendly string:
// 1 -> "1 minute", 45 -> "45 minutes", 60 -> "1 hour", 90 -> "1 hour 30 minutes",
// 848 -> "14 hours 8 minutes". Used by the delete-car cooldown error UI.
export function formatCooldownDuration(totalMinutes) {
  const m = Math.max(0, Math.round(Number(totalMinutes) || 0));
  if (m < 60) return `${m} minute${m === 1 ? '' : 's'}`;
  const hours = Math.floor(m / 60);
  const minutes = m % 60;
  const hourPart = `${hours} hour${hours === 1 ? '' : 's'}`;
  if (minutes === 0) return hourPart;
  return `${hourPart} ${minutes} minute${minutes === 1 ? '' : 's'}`;
}

export function getDefaultRegion() {
  return 'EU';
}
